import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Clock, ExternalLink, Loader2, Send, Trash2, User, Users, XCircle } from "lucide-react";
import {
  WEDDING_NOTIFICATION_KINDS,
  type WeddingNotificationKind,
  type WeddingTelegramChat,
  type WeddingTelegramLinkCode,
  type WeddingTelegramState,
} from "@wedding/shared";

/**
 * "Telegram bildirimleri" — düğünün Ayarlar sayfasındaki ortak kart (organizatör + admin, 2026-10-18).
 * Her app KENDİ uç tabanını verir: organizatör `/api/organizer/w/:slug`, admin `/api/admin/w/:slug`.
 *
 * - "Kendi Telegram'ıma bağla" (`t.me/<bot>?start=`) ya da "Bir gruba ekle" (`?startgroup=`): kod sunucuda,
 *   süresi sunucudan; bağlantı açılınca kart yeni sohbet gelene kadar (süre dolana dek) kendini yeniler.
 * - Bağlı sohbetler: kişisel / grup, olay türü seçimleri (sürümlü yazım — `installMutationFetch` If-Match'i
 *   kendisi ekler), "Kaldır".
 * - Her aksiyon başarı / hata bildirimi verir (kendi toast'u).
 */

type Toast = { msg: string; ok: boolean } | null;
type Pending = { kind: "private" | "group"; link: WeddingTelegramLinkCode; known: string[] };

const POLL_MS = 3000;

class ApiError extends Error {}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", headers: { "Content-Type": "application/json" }, ...init });
  const json = (await res.json().catch(() => null)) as { ok?: boolean; data?: T; error?: string } | null;
  if (!res.ok || !json?.ok) throw new ApiError(json?.error ?? `HTTP ${res.status}`);
  return json.data as T;
}

function countdown(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function WeddingTelegramCard({ apiBase }: { apiBase: string }) {
  const { t } = useTranslation();
  const base = `${apiBase}/telegram`;
  const [state, setState] = useState<WeddingTelegramState | null>(null);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  const load = useCallback(async () => {
    try {
      const next = await call<WeddingTelegramState>(base);
      setState(next);
      setFailed(false);
      return next;
    } catch {
      setFailed(true);
      return null;
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const errorText = useCallback(
    (err: unknown) => {
      const code = err instanceof Error ? err.message : String(err);
      return code === "migration_required" || code === "bot_unavailable"
        ? t(`weddingTelegram.errors.${code}`)
        : t("weddingTelegram.errors.generic", { error: code });
    },
    [t],
  );

  const expiresAt = pending ? Date.parse(pending.link.expiresAt) : 0;
  const expired = pending !== null && now >= expiresAt;

  // Bağlantı açıkken: saniye sayacı + yeni sohbet gelene kadar yenileme (süre dolunca durur).
  useEffect(() => {
    if (!pending || expired) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(async () => {
      const next = await load();
      const fresh = next?.chats.find((ch) => !pending.known.includes(ch.id));
      if (fresh) {
        setPending(null);
        setToast({ msg: t("weddingTelegram.linked", { name: chatName(fresh, t) }), ok: true });
      }
    }, POLL_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [pending, expired, load, t]);

  const connect = async (kind: Pending["kind"]) => {
    setBusy(`link-${kind}`);
    try {
      const link = await call<WeddingTelegramLinkCode>(`${base}/link`, { method: "POST" });
      setNow(Date.now());
      setPending({ kind, link, known: (state?.chats ?? []).map((ch) => ch.id) });
    } catch (err) {
      setToast({ msg: `${t("weddingTelegram.linkFailed")} — ${errorText(err)}`, ok: false });
    } finally {
      setBusy(null);
    }
  };

  const saveEvents = async (chat: WeddingTelegramChat, events: WeddingNotificationKind[]) => {
    setBusy(`events-${chat.id}`);
    const before = state;
    setState((s) => (s ? { ...s, chats: s.chats.map((ch) => (ch.id === chat.id ? { ...ch, events } : ch)) } : s));
    try {
      const updated = await call<WeddingTelegramChat>(`${base}/chats/${chat.id}`, {
        method: "PATCH",
        body: JSON.stringify({ events }),
      });
      setState((s) => (s ? { ...s, chats: s.chats.map((ch) => (ch.id === chat.id ? updated : ch)) } : s));
      setToast({ msg: t("weddingTelegram.saved"), ok: true });
    } catch (err) {
      setState(before);
      if (err instanceof ApiError && err.message === "stale_write") {
        await load();
        setToast({ msg: t("weddingTelegram.stale"), ok: false });
      } else {
        setToast({ msg: `${t("weddingTelegram.saveFailed")} — ${errorText(err)}`, ok: false });
      }
    } finally {
      setBusy(null);
    }
  };

  const remove = async (chat: WeddingTelegramChat) => {
    if (!window.confirm(t("weddingTelegram.removeConfirm", { name: chatName(chat, t) }))) return;
    setBusy(`remove-${chat.id}`);
    try {
      await call(`${base}/chats/${chat.id}`, { method: "DELETE" });
      setState((s) => (s ? { ...s, chats: s.chats.filter((ch) => ch.id !== chat.id) } : s));
      setToast({ msg: t("weddingTelegram.removed"), ok: true });
    } catch (err) {
      setToast({ msg: errorText(err), ok: false });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm" data-testid="wedding-telegram">
      <header className="px-5 py-4 border-b border-border flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Send className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="font-semibold text-sm">{t("weddingTelegram.title")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5 text-pretty">{t("weddingTelegram.subtitle")}</p>
        </div>
      </header>

      <div className="p-5 space-y-5">
        {failed ? (
          <p className="text-sm text-muted-foreground">
            {t("weddingTelegram.loadFailed")}{" "}
            <button type="button" onClick={() => void load()} className="underline underline-offset-2 cursor-pointer">
              {t("weddingTelegram.retry")}
            </button>
          </p>
        ) : !state ? (
          <div className="h-28 rounded-xl bg-muted animate-pulse" data-testid="wedding-telegram-loading" />
        ) : !state.available || !state.botConfigured ? (
          <p className="rounded-xl bg-muted/60 px-4 py-3 text-sm text-muted-foreground">
            {t(state.available ? "weddingTelegram.botMissing" : "weddingTelegram.unavailable")}
          </p>
        ) : (
          <>
            {pending ? (
              <PendingLink pending={pending} remainingMs={expiresAt - now} expired={expired} onCancel={() => setPending(null)} />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <ConnectTile
                  icon={<User className="h-4 w-4" aria-hidden="true" />}
                  title={t("weddingTelegram.connectPrivate")}
                  hint={t("weddingTelegram.connectPrivateHint")}
                  busy={busy === "link-private"}
                  disabled={busy !== null}
                  onClick={() => void connect("private")}
                />
                <ConnectTile
                  icon={<Users className="h-4 w-4" aria-hidden="true" />}
                  title={t("weddingTelegram.connectGroup")}
                  hint={t("weddingTelegram.connectGroupHint")}
                  busy={busy === "link-group"}
                  disabled={busy !== null}
                  onClick={() => void connect("group")}
                />
              </div>
            )}

            <div className="space-y-2.5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t("weddingTelegram.chatsTitle")}
              </h3>
              {state.chats.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("weddingTelegram.empty")}</p>
              ) : (
                <ul className="space-y-2.5">
                  {state.chats.map((chat) => (
                    <ChatRow
                      key={chat.id}
                      chat={chat}
                      busy={busy === `events-${chat.id}` || busy === `remove-${chat.id}`}
                      onToggle={(kind) =>
                        void saveEvents(
                          chat,
                          chat.events.includes(kind) ? chat.events.filter((e) => e !== kind) : [...chat.events, kind],
                        )
                      }
                      onRemove={() => void remove(chat)}
                    />
                  ))}
                </ul>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-pretty">{t("weddingTelegram.footer")}</p>
          </>
        )}
      </div>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={[
            "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium max-w-[calc(100vw-2rem)]",
            toast.ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
          ].join(" ")}
        >
          {toast.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" /> : <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />}
          <span>{toast.msg}</span>
        </div>
      )}
    </section>
  );
}

type T = ReturnType<typeof useTranslation>["t"];

function chatName(chat: WeddingTelegramChat, t: T): string {
  return chat.title || (chat.username ? `@${chat.username}` : t(chat.chatType === "private" ? "weddingTelegram.unnamedPrivate" : "weddingTelegram.unnamedGroup"));
}

function ConnectTile(props: {
  icon: ReactNode;
  title: string;
  hint: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      aria-busy={props.busy}
      className="group flex items-start gap-3 rounded-xl border border-border bg-background px-4 py-3.5 text-left transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
    >
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        {props.busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : props.icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{props.title}</span>
        <span className="block text-xs text-muted-foreground mt-0.5 text-pretty">{props.hint}</span>
      </span>
    </button>
  );
}

function PendingLink({
  pending,
  remainingMs,
  expired,
  onCancel,
}: {
  pending: Pending;
  remainingMs: number;
  expired: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const href = pending.kind === "private" ? pending.link.privateLink : pending.link.groupLink;
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-4 space-y-3" data-testid="wedding-telegram-pending">
      {expired ? (
        <p className="text-sm">{t("weddingTelegram.expired")}</p>
      ) : (
        <>
          <p className="text-sm text-pretty">
            {t(pending.kind === "private" ? "weddingTelegram.waitingPrivate" : "weddingTelegram.waitingGroup")}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              {t("weddingTelegram.openTelegram")}
            </a>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {t("weddingTelegram.expiresIn", { time: countdown(remainingMs) })}
            </span>
          </div>
        </>
      )}
      <button
        type="button"
        onClick={onCancel}
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground cursor-pointer"
      >
        {t("weddingTelegram.cancel")}
      </button>
    </div>
  );
}

function ChatRow({
  chat,
  busy,
  onToggle,
  onRemove,
}: {
  chat: WeddingTelegramChat;
  busy: boolean;
  onToggle: (kind: WeddingNotificationKind) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const name = chatName(chat, t);
  const isPrivate = chat.chatType === "private";
  return (
    <li className="rounded-xl border border-border px-4 py-3 space-y-3">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {isPrivate ? <User className="h-3.5 w-3.5" aria-hidden="true" /> : <Users className="h-3.5 w-3.5" aria-hidden="true" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{name}</p>
          <p className="text-xs text-muted-foreground">
            {t(isPrivate ? "weddingTelegram.private" : "weddingTelegram.group")}
            {chat.username && chat.title ? ` · @${chat.username}` : ""}
          </p>
        </div>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />}
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          aria-label={`${t("weddingTelegram.remove")}: ${name}`}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:text-red-600 hover:bg-red-500/10 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">{t("weddingTelegram.remove")}</span>
        </button>
      </div>
      <fieldset disabled={busy} className="flex flex-wrap gap-2">
        <legend className="sr-only">{t("weddingTelegram.eventsLabel", { name })}</legend>
        {WEDDING_NOTIFICATION_KINDS.map((kind) => {
          const on = chat.events.includes(kind);
          return (
            <label
              key={kind}
              className={[
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs select-none transition-colors cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40",
                on ? "border-primary/50 bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/30",
              ].join(" ")}
            >
              <input type="checkbox" checked={on} onChange={() => onToggle(kind)} className="sr-only" />
              <span aria-hidden="true" className={on ? "text-primary" : "opacity-40"}>
                {on ? "✓" : "+"}
              </span>
              {t(`weddingTelegram.events.${kind}`)}
            </label>
          );
        })}
      </fieldset>
    </li>
  );
}
